import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon, QrCodeIcon } from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../lib/api';

interface VerificationResult {
  success: boolean;
  status: 'VALID' | 'INVALID' | 'ALREADY_USED' | 'EXPIRED' | 'ERROR';
  message: string;
  product?: {
    name: string;
    manufacturer: string;
    category: string;
    batchNumber: string;
    serialNumber: string;
    manufactureDate?: string | null;
    expiryDate: string;
    countryEntryDate?: string | null;
    status: string;
    verificationCount: number;
    firstVerifiedAt: string | null;
    lastVerifiedAt: string | null;
  };
}

const ProductVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [result, setResult] = useState<VerificationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get QR code from URL parameters
  const qrCode = searchParams.get('code') || searchParams.get('qr') || searchParams.get('id');

  useEffect(() => {
    if (qrCode) {
      handleVerification(qrCode, 'QR_CODE');
    }
  }, [qrCode]);

  const handleVerification = async (code: string, method: 'QR_CODE' | 'MANUAL_ENTRY') => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(`${API_BASE_URL}/barcode-verify/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          barcode: code.trim(),
          method: method,
        }),
      });

      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError('Failed to verify product. Please try again.');
      console.error('Verification error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'VALID':
        return <CheckCircleIcon className="w-16 h-16 text-green-500" />;
      case 'ALREADY_USED':
        return <ExclamationTriangleIcon className="w-16 h-16 text-yellow-500" />;
      case 'INVALID':
      case 'EXPIRED':
      case 'ERROR':
        return <XCircleIcon className="w-16 h-16 text-red-500" />;
      default:
        return <QrCodeIcon className="w-16 h-16 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'VALID':
        return 'text-green-600';
      case 'ALREADY_USED':
        return 'text-yellow-600';
      case 'INVALID':
      case 'EXPIRED':
      case 'ERROR':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusMessage = (status: string) => {
    switch (status) {
      case 'VALID':
        return 'Authentic Product';
      case 'ALREADY_USED':
        return 'Previously Verified';
      case 'INVALID':
        return 'Invalid Code';
      case 'EXPIRED':
        return 'Code Expired';
      case 'ERROR':
        return 'Verification Error';
      default:
        return 'Unknown Status';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-28 pb-12">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Verifying product...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <XCircleIcon className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-red-800 mb-2">Verification Failed</h3>
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Verification Result */}
        {result && !loading && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Status Header */}
            <div className={`px-6 py-8 text-center ${result.success ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className="flex justify-center mb-4">
                {getStatusIcon(result.status)}
              </div>
              <h2 className={`text-2xl font-bold mb-2 ${getStatusColor(result.status)}`}>
                {getStatusMessage(result.status)}
              </h2>
              <p className="text-gray-600">{result.message}</p>
            </div>

            {/* Product Details */}
            {result.product && (
              <div className="px-6 py-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-4 divide-y divide-gray-100 md:divide-y-0">
                  <div className="flex items-baseline justify-between gap-4 pt-3 first:pt-0 md:block md:pt-0">
                    <dt className="text-sm font-medium text-gray-500 whitespace-nowrap">Product Name</dt>
                    <dd className="text-sm text-gray-900 font-medium text-right md:text-left">{result.product.name}</dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-4 pt-3 md:block md:pt-0">
                    <dt className="text-sm font-medium text-gray-500 whitespace-nowrap">Manufacturer</dt>
                    <dd className="text-sm text-gray-900 text-right md:text-left">{result.product.manufacturer}</dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-4 pt-3 md:block md:pt-0">
                    <dt className="text-sm font-medium text-gray-500 whitespace-nowrap">Category</dt>
                    <dd className="text-sm text-gray-900 text-right md:text-left">{result.product.category}</dd>
                  </div>
                  {result.product.batchNumber && (
                    <div className="flex items-baseline justify-between gap-4 pt-3 md:block md:pt-0">
                      <dt className="text-sm font-medium text-gray-500 whitespace-nowrap">Batch Number</dt>
                      <dd className="text-sm text-gray-900 font-mono text-right md:text-left">{result.product.batchNumber}</dd>
                    </div>
                  )}
                  {result.product.manufactureDate && (
                    <div className="flex items-baseline justify-between gap-4 pt-3 md:block md:pt-0">
                      <dt className="text-sm font-medium text-gray-500 whitespace-nowrap">Manufacture Date</dt>
                      <dd className="text-sm text-gray-900 text-right md:text-left">{new Date(result.product.manufactureDate).toLocaleDateString()}</dd>
                    </div>
                  )}
                  {result.product.expiryDate && (
                    <div className="flex items-baseline justify-between gap-4 pt-3 md:block md:pt-0">
                      <dt className="text-sm font-medium text-gray-500 whitespace-nowrap">Expiry Date</dt>
                      <dd className="text-sm text-gray-900 text-right md:text-left">{new Date(result.product.expiryDate).toLocaleDateString()}</dd>
                    </div>
                  )}
                  {result.product.countryEntryDate && (
                    <div className="flex items-baseline justify-between gap-4 pt-3 md:block md:pt-0">
                      <dt className="text-sm font-medium text-gray-500 whitespace-nowrap">Date Entered Tanzania</dt>
                      <dd className="text-sm text-gray-900 text-right md:text-left">{new Date(result.product.countryEntryDate).toLocaleDateString()}</dd>
                    </div>
                  )}
                </div>

                {/* Verification Status */}
                <div className="mt-6 p-4 bg-blue-50 rounded-lg space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-blue-900">Distribution Status</dt>
                    <dd className="text-sm text-blue-800 font-medium">{result.product.status}</dd>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-blue-100">
                    <dt className="text-sm font-medium text-blue-900">Verification Count</dt>
                    <dd className="text-sm font-semibold text-blue-800">{result.product.verificationCount}</dd>
                  </div>
                </div>

                {/* Verification History */}
                {(result.product.firstVerifiedAt || result.product.lastVerifiedAt) && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Verification History</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {result.product.firstVerifiedAt && (
                        <div>
                          <dt className="text-gray-500">First Verified</dt>
                          <dd className="text-gray-900">
                            {new Date(result.product.firstVerifiedAt).toLocaleString()}
                          </dd>
                        </div>
                      )}
                      {result.product.lastVerifiedAt && (
                        <div>
                          <dt className="text-gray-500">Last Verified</dt>
                          <dd className="text-gray-900">
                            {new Date(result.product.lastVerifiedAt).toLocaleString()}
                          </dd>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => window.location.reload()}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Verify Another Product
                </button>
                <a
                  href="/"
                  className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors text-center"
                >
                  Back to Home
                </a>
              </div>
            </div>
          </div>
        )}

        {/* No QR Code State */}
        {!qrCode && !loading && !result && (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <QrCodeIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Barcode Detected</h3>
            <p className="text-gray-600 mb-6">
              Please scan the barcode on your product packaging to verify authenticity.
            </p>
            <a
              href="/"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Home
            </a>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>
            For support or questions, contact{' '}
            <a href="mailto:support@dentopharma.co.tz" className="text-blue-600 hover:text-blue-700">
              support@dentopharma.co.tz
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProductVerification;
